import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  bonusLabel,
  formatCents,
  formatPerFolio,
  referencePackage,
  ribbonLabel,
  sortCatalog,
  validityLabel,
} from "@/utils/packageFeatures.js";
import {
  maxFolios,
  recommendPackage,
  savingsCents,
} from "@/utils/landingCatalog.js";
import { Iso, Person } from "./Artwork.jsx";
import { prefersCalm, scrollToSection } from "./motion.js";

const NOTE_AUTO = "La barra se mueve sola. Tócala para elegir tu número.";
const NOTE_MANUAL = "Elige tu número con la barra.";

/** $23 si es cerrado, $18.33 si trae centavos. */
const pesos = (cents) =>
  cents % 100 === 0 ? formatCents(cents) : formatPerFolio(cents);

/**
 * «¿Cuántos folios necesitas?»: la persona mueve la barra y ve el paquete más
 * barato que los cubre. Mientras nadie la toque, la barra se mueve sola, pero
 * sólo con la sección a la vista; al tocarla se detiene y vuelve a moverse
 * nueve segundos después.
 */
const Calculator = ({ packages }) => {
  const { t } = useTranslation();
  const catalog = sortCatalog(packages);
  const max = maxFolios(catalog);
  const [need, setNeed] = useState(30);
  const [auto, setAuto] = useState(true);
  const sectionRef = useRef(null);
  const visible = useRef(false);
  const direction = useRef(1);
  const idle = useRef(null);
  // En un efecto y no al pintar: la portada se pre-genera sin navegador, y el
  // primer pintado tiene que coincidir con ese HTML.
  const [calm, setCalm] = useState(false);
  useEffect(() => setCalm(prefersCalm()), []);

  useEffect(() => {
    if (calm) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        visible.current = entry.isIntersecting;
      },
      { threshold: 0.25 },
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [calm]);

  useEffect(() => {
    if (calm || !auto || max === 0) return undefined;
    const timer = setInterval(() => {
      if (!visible.current) return;
      setNeed((current) => {
        let next = current + direction.current * 2;
        if (next >= max) {
          next = max;
          direction.current = -1;
        } else if (next <= 6) {
          next = 6;
          direction.current = 1;
        }
        return next;
      });
    }, 110);
    return () => clearInterval(timer);
  }, [auto, calm, max]);

  useEffect(() => () => clearTimeout(idle.current), []);

  const takeOver = () => {
    setAuto(false);
    clearTimeout(idle.current);
    idle.current = setTimeout(() => setAuto(true), 9000);
  };

  const pkg = recommendPackage(catalog, need);
  const reference = referencePackage(catalog);
  const previous = pkg ? (catalog[catalog.indexOf(pkg) - 1] ?? null) : null;
  const sheets = Math.max(1, Math.ceil(need / 5));
  const scale = [0, 0.25, 0.5, 0.75, 1].map((k) =>
    Math.max(1, Math.round(max * k)),
  );

  return (
    <section id="calculadora" ref={sectionRef}>
      <div className="lp-center">
        <p className="lp-eyebrow">Calculadora</p>
        <h2>¿Cuántos folios necesitas?</h2>
        <p>
          Mueve la barra: te decimos qué paquete cubre esa cantidad al menor
          precio.
        </p>
      </div>
      {pkg && (
        <div className="lp-calc">
          <div className="lp-in">
            <label
              htmlFor="lp-folios"
              style={{ display: "flex", alignItems: "center", gap: 10 }}
            >
              <Iso size={30} />
              Folios que quieres tener
            </label>
            <div className="lp-big lp-num">{need}</div>
            <input
              id="lp-folios"
              type="range"
              min="1"
              max={max}
              value={need}
              aria-describedby="lp-folios-scale"
              onChange={(event) => setNeed(Number(event.target.value))}
              onPointerDown={takeOver}
              onKeyDown={takeOver}
              onTouchStart={takeOver}
            />
            <div className="lp-scale" id="lp-folios-scale">
              {scale.map((value) => (
                <span key={value}>{value}</span>
              ))}
            </div>
            <div className="lp-stack" aria-hidden="true">
              {Array.from({ length: sheets }, (_, i) => (
                <i
                  className="lp-sheet"
                  key={i}
                  style={{ height: `${46 + i * 5}px` }}
                />
              ))}
            </div>
            <p className="lp-note">{auto && !calm ? NOTE_AUTO : NOTE_MANUAL}</p>
            <p className="lp-note">
              Cada hoja de la pila representa 5 folios. Un cliente que factura 5
              veces al mes usa unos 60 al año.
            </p>
          </div>
          <div className="lp-out">
            <p
              className="lp-eyebrow"
              style={{ color: "inherit", opacity: 0.75 }}
            >
              Te conviene
            </p>
            <div className="lp-rec">{pkg.name}</div>
            <div className="lp-rows">
              <div>
                <span>Precio</span>
                <b className="lp-num">{formatCents(pkg.priceCents)}</b>
              </div>
              <div>
                <span>Folios que recibes</span>
                <b className="lp-num">{pkg.creditedFolios}</b>
              </div>
              <div>
                <span>Costo por folio</span>
                <b className="lp-num">
                  {formatPerFolio(pkg.pricePerFolioCents)}
                </b>
              </div>
              {reference && (
                <div>
                  <span>
                    Ahorro contra {pesos(reference.pricePerFolioCents)} por
                    folio
                  </span>
                  <b className="lp-num">{pesos(savingsCents(pkg, catalog))}</b>
                </div>
              )}
              <div>
                <span>Vigencia</span>
                <b>{validityLabel(pkg, t, "value")}</b>
              </div>
            </div>
            <p style={{ marginTop: 20 }}>
              <button
                type="button"
                className="lp-btn"
                onClick={() => scrollToSection("contacto")}
              >
                Contratar este paquete
              </button>
            </p>
            <div className="lp-agent lp-bob">
              <div className="lp-bubble lp-y">
                {bonusLabel(pkg, t) ??
                  ribbonLabel(pkg, previous, t) ??
                  "Lo justo para lo que facturas"}
              </div>
              <div className="lp-av">
                <Person
                  look={{
                    skin: "#E8B48C",
                    hair: "#5A3A22",
                    shirt: "#4977CC",
                    long: 1,
                    hs: 1,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Calculator;
